import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { ArrowRight, BarChart3, Send, Users, Zap, TrendingUp, Clock } from 'lucide-react';
import { useGateModal } from '../hooks/useGateModal';

const heroStrategies = [
  { name: 'Scalper', cmd: '/scalper', tf: '1m - 5m', icon: Zap, color: 'text-yellow-400', border: 'border-yellow-500/40', telegram: '/images/social-proof/v2-scalper-telegram.png' },
  { name: 'Intraday', cmd: '/intraday', tf: '5m - 15m', icon: TrendingUp, color: 'text-blue-400', border: 'border-blue-500/40', telegram: '/images/social-proof/v2-intraday-telegram.png' },
  { name: 'Swing', cmd: '/swing', tf: '4H - Daily', icon: Clock, color: 'text-purple-400', border: 'border-purple-500/40', telegram: '/images/social-proof/v2-swing-telegram.png' },
];

const Hero = () => {
  const { openGate } = useGateModal();
  const [activeStrategy, setActiveStrategy] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStrategy((prev) => (prev + 1) % heroStrategies.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#1a2c4e] via-tc-bg to-tc-bg" />

      {/* Ambient background glow (no noisy image) */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-tc-bg opacity-80" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            {/* TCA Badge */}
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-tc-surface/50 border border-tc-border mb-8">
              <img src="/tca-logo.png" alt="TCA" className="h-6 w-auto" />
              <span className="text-sm text-tc-text-secondary font-medium">TRADERS COUNCIL ASIA INDONESIA</span>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-extrabold text-white leading-[1.12] tracking-tight mb-6">
              Trading XAUUSD tanpa arah?{' '}
              <span className="text-tc-primary">Bot AI ini kasih setup lengkap</span>
              {' '}&mdash; gratis.
            </h1>

            <p className="text-lg md:text-xl text-tc-text-secondary font-heading font-medium mb-8 max-w-xl">
              Jawab quiz singkat, temukan gaya trading Anda, dan langsung gunakan bot AI di Telegram untuk analisis chart XAUUSD real-time.
            </p>

            {/* Social proof */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex items-center gap-6 mb-8"
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-tc-secondary" />
                <span className="text-tc-text-secondary text-sm">
                  <span className="text-white font-semibold">500+</span> trader sudah bergabung
                </span>
              </div>
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-tc-secondary" />
                <span className="text-tc-text-secondary text-sm">
                  <span className="text-white font-semibold">3</span> strategi tersedia
                </span>
              </div>
            </motion.div>

            {/* Dual CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                onClick={openGate}
                className="bg-tc-primary hover:bg-tc-primary-hover text-white px-8 py-7 text-lg font-bold tracking-wider rounded-full btn-glow group"
                data-testid="hero-telegram-btn"
              >
                <Send className="w-5 h-5 mr-3" />
                CARI TAHU GAYA TRADING ANDA
                <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
            <p className="text-sm text-tc-text-secondary mt-4 ml-1">
              Gratis. Langsung akses bot AI di Telegram.
            </p>
          </motion.div>

          {/* Right Visual — Real Bot Output Showcase */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:block relative"
          >
            {/* Strategy selector pills */}
            <div className="flex gap-2 mb-4 justify-center">
              {heroStrategies.map((strat, idx) => (
                <button
                  key={strat.name}
                  onClick={() => setActiveStrategy(idx)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 border ${
                    activeStrategy === idx
                      ? `${strat.color} ${strat.border} bg-white/5`
                      : 'border-tc-border text-tc-text-secondary hover:text-white'
                  }`}
                >
                  <strat.icon className="w-3.5 h-3.5" />
                  {strat.name}
                </button>
              ))}
            </div>

            {/* Screenshot display */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStrategy}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.25 }}
                className="relative"
              >
                <div className="glass-card rounded-2xl p-3 border border-tc-border overflow-hidden">
                  <div className="flex items-center gap-1.5 px-2 py-1.5 mb-2">
                    <div className={`w-2 h-2 rounded-full ${heroStrategies[activeStrategy].color === 'text-yellow-400' ? 'bg-yellow-500' : heroStrategies[activeStrategy].color === 'text-blue-400' ? 'bg-blue-500' : 'bg-purple-500'}`} />
                    <span className="text-xs text-tc-text-secondary font-medium uppercase tracking-wider">
                      Telegram — {heroStrategies[activeStrategy].cmd}
                    </span>
                  </div>
                  <img
                    src={heroStrategies[activeStrategy].telegram}
                    alt={`${heroStrategies[activeStrategy].name} bot output`}
                    className="w-full rounded-xl"
                  />
                </div>

                <p className="mt-3 text-center text-xs text-tc-text-secondary">
                  Screenshot sebenar dari bot. Bukan mockup.
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Floating badge */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -top-4 -right-4 bg-tc-surface rounded-xl px-4 py-3 border border-tc-border shadow-xl"
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-tc-secondary" />
                <p className="text-tc-secondary font-mono text-sm font-medium">XAUUSD</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
